import { getApiBaseUrl, getEffectiveBackendOrigin } from '@shared/config/apiConfig';
import { clearAuthMeSnapshot } from './sessionMe';

export const AUTH_SESSION_EXPIRED_EVENT = 'ff-auth-session-expired';

/** Banquet tastings run 3–4 hours offline; do not kill the login mid-capture. */
export function isFoodTastingRoute(pathname) {
  const path =
    pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '');
  return typeof path === 'string' && path.startsWith('/food-tasting');
}

const AUTH_TOKEN_KEY = 'ff_auth_token';
const AUTH_USER_KEY = 'ff_auth_user';
const AUTH_API_ORIGIN_KEY = 'ff_auth_api_origin';

/**
 * Normalize API base to protocol + host for comparison (tokens are valid per deployment).
 */
export function normalizeApiOrigin(url) {
  if (!url) return '';
  const s = String(url).trim().replace(/\/+$/, '');
  const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(withProto);
    return `${u.protocol}//${u.host}`;
  } catch {
    return s;
  }
}

export function getStoredAuthApiOrigin() {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(AUTH_API_ORIGIN_KEY);
  } catch {
    return null;
  }
}

function resolveCurrentApiOrigin() {
  const base = getApiBaseUrl();
  if (base) return normalizeApiOrigin(base);
  return normalizeApiOrigin(getEffectiveBackendOrigin());
}

export function setStoredAuthApiOriginFromApiBase() {
  if (typeof window === 'undefined') return;
  try {
    const o = resolveCurrentApiOrigin();
    if (o) localStorage.setItem(AUTH_API_ORIGIN_KEY, o);
  } catch {
    // ignore
  }
}

export function clearStoredAuthApiOrigin() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(AUTH_API_ORIGIN_KEY);
  } catch {
    // ignore
  }
}

/**
 * If a token exists but was issued for a different API host, the JWT is invalid — clear session.
 */
export function sessionTokenMatchesCurrentApi() {
  const token = typeof window !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_KEY) : null;
  if (!token) return true;
  const stored = getStoredAuthApiOrigin();
  const current = resolveCurrentApiOrigin();
  if (!current) return true;
  if (!stored) return false;
  return stored === current;
}

/**
 * Clear token, user, /me snapshot, and API origin binding.
 */
export function clearFullAuthSession() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    clearStoredAuthApiOrigin();
    clearAuthMeSnapshot();
  } catch {
    // ignore
  }
}

export { AUTH_TOKEN_KEY, AUTH_USER_KEY, AUTH_API_ORIGIN_KEY };
